import { useNavigation } from '@react-navigation/native';
import { useCallback, useMemo } from 'react';

import { buildSignedInHref, SignedInLinkableRoute } from '../../routes/linkingHelpers';
import { signedInRoutePaths } from '../../routes/routeDefinitions';
import { SignedInNavigationScreenNavigationProp } from '../../routes/types';

/**
 * Routes that are nested inside CustomerDetailGroup.
 * These routes require special navigation handling.
 */
const CUSTOMER_DETAIL_GROUP_ROUTES = ['CustomerDetail', 'PrivatePensionApplication'] as const;
type CustomerDetailGroupRoute = (typeof CUSTOMER_DETAIL_GROUP_ROUTES)[number];

const isCustomerDetailGroupRoute = (route: string): route is CustomerDetailGroupRoute =>
  CUSTOMER_DETAIL_GROUP_ROUTES.includes(route as CustomerDetailGroupRoute);

/**
 * Hook that provides both a memoized URL href and a navigation function for signed-in routes.
 * This eliminates the need to duplicate params between href building and navigation calls.
 *
 * @example
 * // Basic usage
 * const { href, navigate } = useSignedInNavigation('CustomerDetail', {
 *   customerId: customer.customerId,
 *   accountId: customer.accountId,
 *   accountNumber: customer.accountNumber,
 *   cpfCnpj: customer.cpfCnpj,
 *   cpfCnpjFormatted: customer.cpfCnpjFormatted,
 * });
 *
 * // Use href for Link components
 * <Link to={href}>Ver Cliente</Link>
 *
 * // Use navigate for programmatic navigation
 * <Pressable onPress={navigate}>Ver Cliente</Pressable>
 *
 * @example
 * // Simple route without params
 * const { href, navigate } = useSignedInNavigation('Cockpit');
 * // href => '[prefix_if_present]/cockpit'
 *
 * @example
 * // With additional navigation-only params (like passing full objects)
 * const { href, navigate } = useSignedInNavigation(
 *   'CustomerDetail',
 *   {
 *     customerId: customer.customerId,
 *     accountId: customer.accountId,
 *   },
 *   { customer } // Additional params only passed to navigate, not included in href
 * );
 */
export const useSignedInNavigation = <T extends SignedInLinkableRoute>(
  route: T,
  params?: Parameters<typeof buildSignedInHref<T>>[1],
  additionalNavigationParams?: Record<string, unknown>,
  navigationMode: 'navigate' | 'replace' = 'navigate'
): {
  href: string;
  navigate: () => void;
} => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();

  const href = useMemo(
    () => buildSignedInHref(route, params),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [route, JSON.stringify(params)]
  );

  const navigate = useCallback(() => {
    const fullParams = {
      ...(params as Record<string, unknown>),
      ...additionalNavigationParams,
    };

    if (isCustomerDetailGroupRoute(route)) {
      // Nested routes inside CustomerDetailGroup
      // @ts-expect-error - TypeScript can't infer the correct overload for navigation when using dynamic route names
      navigation[navigationMode]('AppDrawer', {
        screen: 'CustomerDetailGroup',
        params: {
          screen: route,
          params: fullParams,
        },
      });
    } else if (route in signedInRoutePaths) {
      // Regular routes inside AppDrawer
      navigation[navigationMode]('AppDrawer', {
        screen: route,
        params: fullParams,
      } as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation, route, JSON.stringify(params), JSON.stringify(additionalNavigationParams)]);

  return { href, navigate };
};

export default useSignedInNavigation;
